import { createModel, ModelConfig } from '@rematch/core';
import Axios from 'axios';
import { TranslationIDs, QuoteStatuses } from '../../constants';

type Feedback = {
  fromName: string;
  fromEmail: string;
  message: string;
  submitError: string;
  sending: boolean;
};

const changeFromName = function handleChangeFromName(
  state: Feedback,
  fromName: string
): Feedback {
  return {
    ...state,
    fromName
  };
};

const changeFromEmail = function handleChangeFromEmail(
  state: Feedback,
  fromEmail: string
): Feedback {
  return {
    ...state,
    fromEmail
  };
};

const changeMessage = function handleChangeMessage(
  state: Feedback,
  message: string
): Feedback {
  return {
    ...state,
    message
  };
};

const changeSubmitError = function handleChangeSubmitError(
  state: Feedback,
  submitError: string
): Feedback {
  return {
    ...state,
    submitError
  };
};

const changeSending = function handleChangeSending(
  state: Feedback,
  sending: boolean
): Feedback {
  return {
    ...state,
    sending
  };
};

const resetFeedback = function handleResetFeedback(): Feedback {
  return {
    fromName: '',
    fromEmail: '',
    message: '',
    sending: false,
    submitError: ''
  };
};

const isEmailValid = function getIsEmailValid(email: string): boolean {
  const trimmedEmail = email.trim();
  return Boolean(trimmedEmail.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i));
};

const isMessageValid = function getIsMessageValid(
  message: string,
  quoteStatus: string
): boolean {
  if (quoteStatus === QuoteStatuses.ChangesRequested) {
    return message.length > 0;
  }

  return true;
};

const isModelValid = function determineIsModelValid(
  state: Feedback,
  quoteStatus: string
): boolean {
  return (
    Boolean(state) &&
    Boolean(state.fromEmail) &&
    isEmailValid(state.fromEmail) &&
    Boolean(state.fromName) &&
    state.fromName.length > 0 &&
    isMessageValid(state.message, quoteStatus)
  );
};

const feedback: ModelConfig<Feedback> = createModel({
  state: {
    fromName: '',
    fromEmail: '',
    message: '',
    submitError: '',
    sending: false
  },
  reducers: {
    changeFromName,
    changeFromEmail,
    changeMessage,
    changeSubmitError,
    changeSending,
    resetFeedback
  },
  effects: dispatch => ({
    async saveQuoteFeedback(status: string, rootState): Promise<boolean> {
      try {
        dispatch.feedback.changeSubmitError('');
        dispatch.feedback.changeSending(true);

        if (!isModelValid(rootState.feedback, rootState.quote.status)) {
          dispatch.feedback.changeSubmitError(
            TranslationIDs.FeedbackModelInvalidError
          );
          return false;
        }

        const a = Axios.create({
          baseURL: window.AutoQuotes.config.apiBaseUrl
        });

        await a.post(`quote/${rootState.quote.id}/feedback`, {
          Status: status,
          ResponderName: rootState.feedback.fromName,
          ResponderEmail: rootState.feedback.fromEmail.trim(),
          Message: rootState.feedback.message,
          ProjectName: rootState.quote.projectName,
          ProjectTitle: rootState.quote.title,
          Culture: rootState.quote.culture
        });

        dispatch.dialog.setOpenDialog('AcceptQuote');
        dispatch.feedback.resetFeedback();
        return true;
      } catch (e) {
        if (e instanceof Error && e.message && e.message.length > 0) {
          dispatch.feedback.changeSubmitError(e.message);
        } else {
          dispatch.feedback.changeSubmitError(TranslationIDs.UnexpectedError);
        }
        return false;
      } finally {
        dispatch.feedback.changeSending(false);
      }
    }
  })
});

export default feedback;
